'use client';

import React from 'react';
import Link from 'next/link';
import { toast } from 'sonner';

const plans = [
  {
    id: 'plan-free',
    name: 'Free',
    price: '£0',
    period: '',
    description: 'Try ReviewReply AI risk-free. No card required.',
    cta: 'Start for free',
    ctaHref: '/sign-up-login-screen',
    featured: false,
    features: [
      '5 replies per month',
      'Google Reviews support',
      'All 4 response tones',
      'Copy to clipboard',
      '7-day reply history',
    ],
    missing: ['TripAdvisor support', 'Unlimited history', 'Priority generation'],
  },
  {
    id: 'plan-starter',
    name: 'Starter',
    price: '£19',
    period: '/month',
    description: 'For independent restaurants handling reviews daily.',
    cta: 'Start Starter plan',
    ctaHref: '/sign-up-login-screen',
    featured: true,
    badge: 'Most popular',
    features: [
      '150 replies per month',
      'Google & TripAdvisor',
      'All 4 response tones',
      'Full reply history',
      'CSV export',
      'Priority generation',
    ],
    missing: ['Multi-location support'],
  },
  {
    id: 'plan-pro',
    name: 'Pro',
    price: '£49',
    period: '/month',
    description: 'For restaurant groups and hospitality managers.',
    cta: 'Talk to us',
    ctaHref: null,
    featured: false,
    features: [
      'Unlimited replies',
      'Google & TripAdvisor',
      'All 4 response tones',
      'Full reply history',
      'CSV export',
      'Priority generation',
      'Up to 5 locations',
      'Dedicated onboarding',
    ],
    missing: [],
  },
];

export default function PricingSection() {
  return (
    <section id="pricing" className="py-20 lg:py-28">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="section-label mb-3">Pricing</p>
          <h2 className="font-serif text-display-md mb-4" style={{ color: 'var(--foreground)' }}>
            Simple pricing,{' '}
            <span style={{ color: 'var(--primary)', fontStyle: 'italic' }}>no surprises</span>
          </h2>
          <p className="text-base leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
            Start free, upgrade when you need more. Cancel anytime.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={plan.featured ? 'pricing-card pricing-card-featured relative' : 'pricing-card relative'}
            >
              {plan.badge && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full"
                  style={{ backgroundColor: 'var(--accent)', color: '#1A0A02' }}
                >
                  {plan.badge}
                </div>
              )}

              <div className="mb-6">
                <p
                  className="text-sm font-semibold mb-1"
                  style={{ color: plan.featured ? 'rgba(253,250,246,0.7)' : 'var(--muted-foreground)' }}
                >
                  {plan.name}
                </p>
                <div className="flex items-end gap-1 mb-2">
                  <span
                    className="text-4xl font-bold tabular-nums font-serif"
                    style={{ color: plan.featured ? '#FDFAF6' : 'var(--foreground)' }}
                  >
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span
                      className="text-sm mb-1"
                      style={{ color: plan.featured ? 'rgba(253,250,246,0.7)' : 'var(--muted-foreground)' }}
                    >
                      {plan.period}
                    </span>
                  )}
                </div>
                <p
                  className="text-xs leading-relaxed"
                  style={{ color: plan.featured ? 'rgba(253,250,246,0.8)' : 'var(--muted-foreground)' }}
                >
                  {plan.description}
                </p>
              </div>

              <ul className="space-y-2.5 mb-8">
                {plan.features.map((feature) => (
                  <li key={`${plan.id}-feat-${feature}`} className="flex items-start gap-2 text-sm">
                    <svg
                      className="w-4 h-4 mt-0.5 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      style={{ color: plan.featured ? 'var(--accent)' : 'var(--primary)' }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span style={{ color: plan.featured ? '#FDFAF6' : 'var(--foreground)' }}>
                      {feature}
                    </span>
                  </li>
                ))}
                {plan.missing.map((feature) => (
                  <li key={`${plan.id}-miss-${feature}`} className="flex items-start gap-2 text-sm opacity-40">
                    <svg
                      className="w-4 h-4 mt-0.5 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span style={{ color: plan.featured ? '#FDFAF6' : 'var(--foreground)' }}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {plan.ctaHref ? (
                <Link
                  href={plan.ctaHref}
                  className="block text-center py-3 px-6 rounded-xl text-sm font-semibold transition-all duration-150"
                  style={{
                    backgroundColor: plan.featured ? '#FDFAF6' : 'var(--primary)',
                    color: plan.featured ? 'var(--primary)' : '#FDFAF6',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
                    (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(26,10,2,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.transform = '';
                    (e.currentTarget as HTMLElement).style.boxShadow = '';
                  }}
                >
                  {plan.cta}
                </Link>
              ) : (
                <button
                  onClick={() => toast.info('Our team will be in touch shortly. Email us at hello@reviewreply.ai')}
                  className="w-full py-3 px-6 rounded-xl text-sm font-semibold transition-all duration-150 border"
                  style={{
                    backgroundColor: 'transparent',
                    color: 'var(--primary)',
                    borderColor: 'var(--primary)',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(193, 68, 14, 0.06)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                  }}
                >
                  {plan.cta}
                </button>
              )}
            </div>
          ))}
        </div>

        <p className="text-center text-xs mt-8" style={{ color: 'var(--muted-foreground)' }}>
          All prices include VAT where applicable. Cancel anytime from your account settings.
        </p>
      </div>
    </section>
  );
}